/**
 * The pinned-hull goal (M10 P5a2a, plan 2026-10-17-m10-p5a §3.3, hull tiers spec §6.8 "Pin this ship"): the hull the
 * player pinned in a shipyard (`Career.pinnedHull`, P5a0's key) and the gate steps still missing to buy it, as one
 * pinned chip of the top-centre HUD status stack (`hud/status.ts`).
 *
 * - **One pin:** `pinHull` writes `Career.pinnedHull`; pinning another hull replaces it (its `posted` list goes with
 *   it); re-pinning the same hull at the same yard keeps the pin as it is. `clearPinOnPurchase` drops the pin when that
 *   hull is bought.
 * - **The steps** are `gateStatus`'s missing steps (`ui/fleet/models/gates.ts`) read from the Career each time, so a
 *   step ticks off live when the standing crosses the gate, the lease is bought or the company founded. The standing a
 *   gate reads is the maker's (a licence build: the pinned yard owner's, `GoalContext.ownerOf`).
 * - **Keys off:** while `shipyard.json enabled` is false there is no goal (`goalModel` is null) and the chip stays
 *   hidden, so the HUD is today's.
 * - `goalStepKey` names a step for the Wire's `posted` list (P5a2b).
 *
 * Event-driven, like the hail prompt: the host calls `writeGoal(view, goalModel(...))` on a Career change (a standing,
 * the lease, founding, a pin), not per frame; the DOM is written only when the line changes.
 */
import type { Career } from '../career/types'
import { panelCatalog } from '../ui/fleet/models/catalog'
import { gateHullOf, gateLine, gatePlayerOf, gateStatus, shipyardRules, type GateStep, type ShipyardRules } from '../ui/fleet/models/gates'
import { nameOfFaction } from '../ui/fleet/models/shipyard'
import { hudStatus, statusHide, statusShow, STATUS_HIDDEN, type StatusStack } from './status'

/** Pins `hull` at the shipyard of `stationId` (one pin: another hull or yard replaces it; the same keeps it). */
export function pinHull(c: Career, hull: string, stationId: string): Career {
  const p = c.pinnedHull
  if (p !== undefined && p.hull === hull && p.stationId === stationId) return c
  return { ...c, pinnedHull: { hull, stationId } }
}

/** Removes the pin (a Career without one is returned as is). */
export function unpinHull(c: Career): Career {
  if (c.pinnedHull === undefined) return c
  const rest = { ...c }
  delete rest.pinnedHull
  return rest
}

/** The pin clears when its hull is bought; buying another hull leaves it. */
export function clearPinOnPurchase(c: Career, boughtHull: string): Career {
  return c.pinnedHull?.hull === boughtHull ? unpinHull(c) : c
}

/** The Wire's key for a step (P5a2b's `posted` list): the step kind. */
export function goalStepKey(step: GateStep): string {
  return step.kind
}

/** What the goal needs beyond the Career. */
export interface GoalContext {
  /** A founded company (not a sole trader). */
  corporation: boolean
  /** The strategic owner of station `id` (the pinned yard), or null when it isn't known. */
  ownerOf(stationId: string): string | null
}

export interface GoalStepView {
  key: string
  text: string
}

export interface GoalModel {
  hull: string
  name: string
  steps: GoalStepView[]
  /** The chip's line: "Goal: Danube · Lease must be owned · Meridian standing 25 (you: 12)". */
  text: string
}

function hullName(id: string): string {
  return panelCatalog().hullById.get(id)?.name ?? id
}

/** The pinned hull's goal now, or null: no pin, the gates off, or a hull that isn't a player hull (no band). */
export function goalModel(c: Pick<Career, 'pinnedHull' | 'lease' | 'standings'>, ctx: GoalContext, rules: ShipyardRules = shipyardRules()): GoalModel | null {
  const pin = c.pinnedHull
  if (pin === undefined || !rules.enabled) return null
  const row = gateHullOf(pin.hull)
  if (row === null) return null
  const player = gatePlayerOf(c, ctx.corporation)
  const owner = ctx.ownerOf(pin.stationId)
  const st = gateStatus(player, row, { owner, standing: owner === null ? 0 : player.standingOf(owner) }, rules)
  const steps = st.missing.map((m): GoalStepView => ({ key: goalStepKey(m), text: gateLine(m, nameOfFaction) }))
  const name = hullName(pin.hull)
  const tail = steps.length === 0 ? 'every step met: buy it at the yard' : steps.map((s) => s.text).join(' · ')
  return { hull: pin.hull, name, steps, text: `Goal: ${name} · ${tail}` }
}

/** The chip's fields the view writes (an `HTMLElement` in the game, a plain object in tests). */
export interface GoalElement {
  textContent: string | null
  readonly dataset: Record<string, string | undefined>
  readonly style: { transform: string }
}

export interface GoalView {
  readonly el: GoalElement
  readonly stack: StatusStack
  /** The line the DOM shows ('' while hidden; diffed). */
  shown: string
  /** DOM writes so far (tests). */
  writes: number
}

export function createGoalView(el: GoalElement, stack: StatusStack = hudStatus): GoalView {
  el.dataset.on = '0'
  el.textContent = ''
  el.style.transform = STATUS_HIDDEN
  return { el, stack, shown: '', writes: 0 }
}

/** Creates the goal chip under the HUD root, as a pinned status-stack chip. */
export function mountGoalChip(parent: HTMLElement, stack: StatusStack = hudStatus): GoalView & { readonly el: HTMLElement } {
  const el = parent.ownerDocument.createElement('div')
  el.className = 'hud-chip hud-goal-chip'
  el.setAttribute('data-testid', 'hud-goal')
  el.setAttribute('role', 'status')
  parent.appendChild(el)
  const v = createGoalView(el, stack)
  return { ...v, el }
}

/** Shows the goal (a pinned chip) or hides it (null); writes the DOM only when the line changed. */
export function writeGoal(v: GoalView, m: GoalModel | null): void {
  const text = m === null ? '' : m.text
  if (text === v.shown) return
  v.shown = text
  v.writes++
  if (m === null) {
    statusHide(v.stack, v.el)
    v.el.dataset.on = '0'
    v.el.textContent = ''
    return
  }
  v.el.dataset.steps = String(m.steps.length)
  v.el.textContent = text
  statusShow(v.stack, v.el, true)
}
